/**
 * Copy FROM Location Modal
 * Pull Pattern: User selects SOURCE location to copy standards FROM
 * Rendered via portal so it centers in the viewport (not affected by drawer transform).
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { SECTION_ORDER, SECTION_NAMES } from './constants/cleaningStandardsDrawerConstants';
import {
  INFO_CARD_WRAPPER,
  INFO_CARD_WRAPPER_P5,
  INFO_CARD_LABEL,
  INFO_CARD_TITLE,
  INFO_CARD_TEXT,
  INFO_CARD_TAG,
} from './constants/infoCardStyles';

export const CopyFromLocationModal = ({
  isOpen,
  onClose,
  onConfirm,
  targetLocations,
  availableSources,
}) => {
  const [selectedSourceId, setSelectedSourceId] = useState('');

  const handleConfirm = () => {
    if (selectedSourceId) {
      onConfirm(selectedSourceId);
      onClose();
    }
  };

  const selectedSource = availableSources.find((loc) => loc.id === selectedSourceId);

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="copy-standards-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ isolation: 'isolate' }}
        >
          {/* Backdrop - full viewport, same as Duplicate Facility / Duplicate Area */}
          <div
            className="absolute inset-0 bg-[rgba(184,182,182,0.5)]"
            onClick={onClose}
            aria-hidden
          />

          {/* Modal card - centered in viewport via flex parent */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-[580px] max-h-[90vh] overflow-y-auto bg-white rounded-[10px] px-8 py-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
              <div className="flex flex-col gap-6">
                {/* Header - white, icon in circle, black title, grey close (system style) */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#61b8e7]/10 flex items-center justify-center">
                      <Copy className="w-5 h-5 text-[#61b8e7]" strokeWidth={2.5} />
                    </div>
                    <h2 className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black">
                      Copy Standards
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F2F4F8] hover:bg-gray-300 transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5 text-[#1C1B1F]" />
                  </button>
                </div>

                {/* Target areas - shared info card style */}
                <div className={INFO_CARD_WRAPPER}>
                  <p className={INFO_CARD_LABEL}>
                    Applying to {targetLocations.length} area
                    {targetLocations.length !== 1 ? 's' : ''}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {targetLocations.map((loc) => (
                      <span key={loc.id} className={INFO_CARD_TAG}>
                        {loc.name}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Source Selection */}
                <div>
                  <label className="block font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-gray-700 mb-3">
                    Select source area:
                  </label>

                  {availableSources.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-amber-900 mb-1">
                          No configured areas available
                        </p>
                        <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-amber-700">
                          Configure at least one area first to copy its standards.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {availableSources.map((source) => (
                        <button
                          key={source.id}
                          type="button"
                          onClick={() => setSelectedSourceId(source.id)}
                          className={`
                            w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all
                            ${
                              selectedSourceId === source.id
                                ? 'bg-[#61b8e7]/10 border-[#61b8e7] shadow-sm'
                                : 'bg-white border-gray-200 hover:border-gray-300'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div
                                className={`
                                  w-5 h-5 rounded-full border-2 transition-all
                                  ${
                                    selectedSourceId === source.id
                                      ? 'border-[#61b8e7] bg-white'
                                      : 'border-gray-400 bg-white'
                                  }
                                `}
                              >
                                {selectedSourceId === source.id && (
                                  <div className="absolute inset-[3px] bg-[#61b8e7] rounded-full" />
                                )}
                              </div>
                            </div>
                            <span className="font-['Roboto:Medium',sans-serif] font-medium text-[15px] text-black">
                              {source.name}
                            </span>
                          </div>
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-300 font-['Roboto:Medium',sans-serif] font-medium text-[11px]">
                            <Check className="w-3.5 h-3.5 shrink-0" aria-hidden />
                            Configured
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Preview - shared info card style */}
                {selectedSource && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className={INFO_CARD_WRAPPER_P5}
                  >
                    <p className={`${INFO_CARD_TITLE} mb-3`}>
                      Standards from {selectedSource.name}:
                    </p>
                    <div className={`space-y-2 ${INFO_CARD_TEXT}`}>
                      {SECTION_ORDER.map((sectionId) => (
                        <p key={sectionId}>• {SECTION_NAMES[sectionId] ?? sectionId}</p>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Actions - same as Duplicate Facility / Duplicate Area */}
                <div className="flex gap-3 justify-end pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="border-2 border-gray-300 bg-white text-gray-700 rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!selectedSourceId}
                    className="flex items-center justify-center gap-2 rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] transition-colors disabled:cursor-not-allowed bg-[#61b8e7] text-white hover:bg-[#4da3d1] disabled:bg-gray-300 disabled:text-gray-500"
                  >
                    <Copy className="w-4 h-4" strokeWidth={2.5} />
                    Apply to {targetLocations.length} area
                    {targetLocations.length !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modalContent, document.body);
};
